// Pure-function moment.js replacement backed by native Date/Intl — covers only
// the moment/moment-timezone API surface actually used in this codebase (see the
// usage audit before this file was added). Not a general-purpose date library,
// and deliberately not a fluent/chainable wrapper: most call sites just format a
// date, and Date objects already support native comparison operators (<, >, <=,
// >=), so there's no need for isBefore/isAfter/clone-style methods.

export type DateInput = Date | string | number | null | undefined;

type Unit =
  | "year"
  | "years"
  | "quarter"
  | "quarters"
  | "month"
  | "months"
  | "week"
  | "weeks"
  | "day"
  | "days"
  | "hour"
  | "hours"
  | "minute"
  | "minutes"
  | "second"
  | "seconds";

type NormalizedUnit =
  "year" | "quarter" | "month" | "week" | "day" | "hour" | "minute" | "second";

interface DateOpts {
  /** Read/write calendar fields in UTC instead of the local timezone. */
  utc?: boolean;
}

// Bare ISO date forms ("2025", "2025-08", "2025-08-01") have no UTC offset, so per
// the ISO 8601 spec native `new Date(str)` parses them as UTC. Moment's ISO parser
// treats the same strings as LOCAL time when no offset is present. We replicate
// moment's behavior here so date-only strings round-trip without an off-by-one-day
// shift depending on the reader's timezone.
const ISO_DATE_ONLY_RE = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/;

function normalizeUnit(unit: Unit): NormalizedUnit {
  const stripped = unit.endsWith("s") ? unit.slice(0, -1) : unit;
  return stripped as NormalizedUnit;
}

function pad(n: number, len = 2): string {
  return String(n).padStart(len, "0");
}

function ordinal(n: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] ?? suffixes[v] ?? suffixes[0]}`;
}

function daysInMonth(year: number, monthIndex0: number, utc: boolean): number {
  return utc
    ? new Date(Date.UTC(year, monthIndex0 + 1, 0)).getUTCDate()
    : new Date(year, monthIndex0 + 1, 0).getDate();
}

function resetTimeToZero(d: Date, utc: boolean): void {
  if (utc) d.setUTCHours(0, 0, 0, 0);
  else d.setHours(0, 0, 0, 0);
}

function parts(date: Date, utc: boolean) {
  return {
    year: utc ? date.getUTCFullYear() : date.getFullYear(),
    month: utc ? date.getUTCMonth() : date.getMonth(), // 0-based, matches moment
    dayOfMonth: utc ? date.getUTCDate() : date.getDate(),
    weekday: utc ? date.getUTCDay() : date.getDay(), // 0 = Sunday
    hours: utc ? date.getUTCHours() : date.getHours(),
    minutes: utc ? date.getUTCMinutes() : date.getMinutes(),
    seconds: utc ? date.getUTCSeconds() : date.getSeconds(),
  };
}

function namedPart(
  date: Date,
  utc: boolean,
  options: Intl.DateTimeFormatOptions,
  partType: Intl.DateTimeFormatPartTypes,
): string {
  const formatted = new Intl.DateTimeFormat("en-US", {
    ...options,
    timeZone: utc ? "UTC" : undefined,
  }).formatToParts(date);
  return formatted.find((p) => p.type === partType)?.value ?? "";
}

/**
 * Parses API/date-picker input into a Date, replicating moment's local-time
 * (not UTC) interpretation of bare ISO date-only strings, and moment's
 * tolerance of missing input: `undefined` resolves to now (moment(undefined)
 * is a valid "now" moment), `null` resolves to an Invalid Date (moment(null)
 * is invalid but never throws — formatting it just yields "Invalid date").
 * `format` supports only "HH:mm" — the one custom parse format this codebase
 * actually uses.
 */
export function parseDate(
  input: DateInput,
  opts: { format?: "HH:mm" } = {},
): Date {
  if (input === undefined) return new Date();
  if (input === null) return new Date(NaN);
  if (input instanceof Date) return new Date(input.getTime());
  if (typeof input === "number") return new Date(input);

  if (opts.format === "HH:mm") {
    const [h, m] = input.split(":").map(Number);
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m);
  }

  const isoMatch = input.match(ISO_DATE_ONLY_RE);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = isoMatch[2] ? Number(isoMatch[2]) - 1 : 0;
    const day = isoMatch[3] ? Number(isoMatch[3]) : 1;
    return new Date(year, month, day);
  }

  return new Date(input);
}

// Ordered so multi-character tokens are tried before their prefixes
// (e.g. "YYYY" before "YY", "MMMM" before "MMM" before "MM").
const TOKEN_RE = /YYYY|YY|MMMM|MMM|MM|Do|DD|D|dddd|ddd|HH|hh|h|mm|ss|A|a|Z|z/g;

/** UTC offset for the "Z" token, e.g. "+00:00" or "-04:00". */
function utcOffsetString(date: Date, utc: boolean): string {
  const totalMinutes = utc ? 0 : -date.getTimezoneOffset();
  const sign = totalMinutes < 0 ? "-" : "+";
  const abs = Math.abs(totalMinutes);
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/** Formats a date using the moment-style tokens already in use across the app. */
export function formatDate(
  input: DateInput,
  pattern = "YYYY-MM-DDTHH:mm:ssZ",
  opts: DateOpts = {},
): string {
  const utc = opts.utc ?? false;
  const date = parseDate(input);
  // Matches moment: an invalid date always formats as the literal string
  // "Invalid date", regardless of pattern — and this must happen before any
  // Intl.DateTimeFormat call below, since Intl throws on a NaN timestamp.
  if (isNaN(date.getTime())) return "Invalid date";
  const p = parts(date, utc);
  const hour12 = p.hours % 12 === 0 ? 12 : p.hours % 12;
  const meridiem = p.hours < 12 ? "AM" : "PM";

  return pattern.replace(TOKEN_RE, (token) => {
    switch (token) {
      case "YYYY":
        return String(p.year);
      case "YY":
        return pad(p.year % 100);
      case "MMMM":
        return namedPart(date, utc, { month: "long" }, "month");
      case "MMM":
        return namedPart(date, utc, { month: "short" }, "month");
      case "MM":
        return pad(p.month + 1);
      case "Do":
        return ordinal(p.dayOfMonth);
      case "DD":
        return pad(p.dayOfMonth);
      case "D":
        return String(p.dayOfMonth);
      case "dddd":
        return namedPart(date, utc, { weekday: "long" }, "weekday");
      case "ddd":
        return namedPart(date, utc, { weekday: "short" }, "weekday");
      case "HH":
        return pad(p.hours);
      case "hh":
        return pad(hour12);
      case "h":
        return String(hour12);
      case "mm":
        return pad(p.minutes);
      case "ss":
        return pad(p.seconds);
      case "A":
        return meridiem;
      case "a":
        return meridiem.toLowerCase();
      case "Z":
        return utcOffsetString(date, utc);
      case "z":
        return namedPart(
          date,
          utc,
          { hour: "numeric", timeZoneName: "short" },
          "timeZoneName",
        );
      default:
        return token;
    }
  });
}

function applyDeltaInternal(
  date: Date,
  amount: number,
  unit: Unit,
  utc: boolean,
): Date {
  const d = new Date(date.getTime());
  const p = parts(date, utc);

  switch (normalizeUnit(unit)) {
    case "year":
      return applyDeltaInternal(date, amount * 12, "month", utc);
    case "quarter":
      return applyDeltaInternal(date, amount * 3, "month", utc);
    case "month": {
      const day = p.dayOfMonth;
      // Set to the 1st first so month rollover can't be perturbed by the
      // current day-of-month overflowing into the following month.
      if (utc) d.setUTCDate(1);
      else d.setDate(1);
      if (utc) d.setUTCMonth(p.month + amount);
      else d.setMonth(p.month + amount);
      const targetYear = utc ? d.getUTCFullYear() : d.getFullYear();
      const targetMonth = utc ? d.getUTCMonth() : d.getMonth();
      // Clamp to the end of the target month, matching moment (Jan 31 + 1
      // month -> Feb 28), rather than native Date's overflow into March.
      const clampedDay = Math.min(
        day,
        daysInMonth(targetYear, targetMonth, utc),
      );
      if (utc) d.setUTCDate(clampedDay);
      else d.setDate(clampedDay);
      return d;
    }
    case "week":
      return applyDeltaInternal(date, amount * 7, "day", utc);
    case "day":
      if (utc) d.setUTCDate(d.getUTCDate() + amount);
      else d.setDate(d.getDate() + amount);
      return d;
    // Exact-duration units (unlike day/week/month/year) are not calendar-
    // relative, so they're added as plain milliseconds regardless of `utc`
    // — matching moment, where e.g. adding 2 hours across a DST fall-back
    // always advances the clock by exactly 2*3600000ms, never by wall-clock
    // field arithmetic (which can land an hour off across the repeated
    // hour), and fractional amounts (e.g. 1.5 hours) aren't truncated.
    case "hour":
      return new Date(date.getTime() + amount * 60 * 60 * 1000);
    case "minute":
      return new Date(date.getTime() + amount * 60 * 1000);
    case "second":
      return new Date(date.getTime() + amount * 1000);
  }
}

export function addToDate(
  input: DateInput,
  amount: number,
  unit: Unit,
  opts: DateOpts = {},
): Date {
  return applyDeltaInternal(parseDate(input), amount, unit, opts.utc ?? false);
}

export function subtractFromDate(
  input: DateInput,
  amount: number,
  unit: Unit,
  opts: DateOpts = {},
): Date {
  return addToDate(input, -amount, unit, opts);
}

function startOfInternal(date: Date, unit: Unit, utc: boolean): Date {
  const d = new Date(date.getTime());
  const p = parts(date, utc);

  switch (normalizeUnit(unit)) {
    case "year":
      if (utc) d.setUTCMonth(0, 1);
      else d.setMonth(0, 1);
      resetTimeToZero(d, utc);
      break;
    case "quarter": {
      const quarterStartMonth = Math.floor(p.month / 3) * 3;
      if (utc) d.setUTCMonth(quarterStartMonth, 1);
      else d.setMonth(quarterStartMonth, 1);
      resetTimeToZero(d, utc);
      break;
    }
    case "month":
      if (utc) d.setUTCDate(1);
      else d.setDate(1);
      resetTimeToZero(d, utc);
      break;
    case "week":
      // Sunday-start week, matching moment's default (en) locale.
      if (utc) d.setUTCDate(d.getUTCDate() - p.weekday);
      else d.setDate(d.getDate() - p.weekday);
      resetTimeToZero(d, utc);
      break;
    case "day":
      resetTimeToZero(d, utc);
      break;
    case "hour":
      if (utc) d.setUTCMinutes(0, 0, 0);
      else d.setMinutes(0, 0, 0);
      break;
    case "minute":
      if (utc) d.setUTCSeconds(0, 0);
      else d.setSeconds(0, 0);
      break;
    case "second":
      if (utc) d.setUTCMilliseconds(0);
      else d.setMilliseconds(0);
      break;
  }
  return d;
}

export function startOfUnit(
  input: DateInput,
  unit: Unit,
  opts: DateOpts = {},
): Date {
  return startOfInternal(parseDate(input), unit, opts.utc ?? false);
}

export function endOfUnit(
  input: DateInput,
  unit: Unit,
  opts: DateOpts = {},
): Date {
  const utc = opts.utc ?? false;
  const start = startOfInternal(parseDate(input), unit, utc);
  const next = applyDeltaInternal(start, 1, normalizeUnit(unit), utc);
  return new Date(next.getTime() - 1);
}

// Whole calendar months between a and b, plus the leftover ms once those
// whole months are subtracted back out (same sign as `months`, or 0).
function calendarMonthDiffWithRemainder(
  a: Date,
  b: Date,
  utc: boolean,
): { months: number; remainderMs: number } {
  const pa = parts(a, utc);
  const pb = parts(b, utc);
  let months = (pa.year - pb.year) * 12 + (pa.month - pb.month);
  let shifted = applyDeltaInternal(b, months, "month", utc);
  let remainderMs = a.getTime() - shifted.getTime();
  if (months > 0 && remainderMs < 0) {
    months -= 1;
    shifted = applyDeltaInternal(b, months, "month", utc);
    remainderMs = a.getTime() - shifted.getTime();
  } else if (months < 0 && remainderMs > 0) {
    months += 1;
    shifted = applyDeltaInternal(b, months, "month", utc);
    remainderMs = a.getTime() - shifted.getTime();
  }
  return { months, remainderMs };
}

function calendarMonthDiff(a: Date, b: Date, utc: boolean): number {
  return calendarMonthDiffWithRemainder(a, b, utc).months;
}

/** Calendar-aware diff (a - b): whole months/quarters/years account for
 * day-of-month, not just millisecond division. */
export function diffBetween(
  inputA: DateInput,
  inputB: DateInput,
  unit: Unit = "seconds",
  opts: DateOpts = {},
): number {
  const utc = opts.utc ?? false;
  const a = parseDate(inputA);
  const b = parseDate(inputB);
  const ms = a.getTime() - b.getTime();
  // Local day/week counts are calendar-relative, so a range crossing a DST
  // transition needs the offset change backed out first — otherwise the
  // extra/missing hour makes a 90-day range read as 89 or 91 (matches
  // moment's own "negate dst" correction in its diff() implementation).
  const dstDeltaMs = utc
    ? 0
    : (a.getTimezoneOffset() - b.getTimezoneOffset()) * 60000;

  switch (normalizeUnit(unit)) {
    case "year":
      return Math.trunc(calendarMonthDiff(a, b, utc) / 12);
    case "quarter":
      return Math.trunc(calendarMonthDiff(a, b, utc) / 3);
    case "month":
      return calendarMonthDiff(a, b, utc);
    case "week":
      return Math.trunc((ms - dstDeltaMs) / (7 * 24 * 60 * 60 * 1000));
    case "day":
      return Math.trunc((ms - dstDeltaMs) / (24 * 60 * 60 * 1000));
    case "hour":
      return Math.trunc(ms / (60 * 60 * 1000));
    case "minute":
      return Math.trunc(ms / (60 * 1000));
    case "second":
      return Math.trunc(ms / 1000);
  }
}

/**
 * Compares two dates truncated to a calendar unit (e.g. "same day"). For plain
 * before/after/same-instant comparisons, just use native `<`, `>`, `<=`, `>=`
 * on Date objects directly — no helper needed.
 */
export function compareByUnit(
  inputA: DateInput,
  inputB: DateInput,
  unit: Unit,
  opts: DateOpts = {},
): number {
  const utc = opts.utc ?? false;
  const a = startOfInternal(parseDate(inputA), unit, utc);
  const b = startOfInternal(parseDate(inputB), unit, utc);
  return a.getTime() - b.getTime();
}

export function minDate(...inputs: DateInput[]): Date {
  if (inputs.length === 0) return new Date();
  return inputs
    .map((i) => parseDate(i))
    .reduce((min, d) => (d < min ? d : min));
}

export function maxDate(...inputs: DateInput[]): Date {
  if (inputs.length === 0) return new Date();
  return inputs
    .map((i) => parseDate(i))
    .reduce((max, d) => (d > max ? d : max));
}

const DAY_MS = 24 * 60 * 60 * 1000;
// 400 Gregorian years have 146097 days and 4800 months — the fixed average
// month length moment itself uses to turn a leftover-days remainder into a
// fractional month (see moment's `monthsToDays`/`daysToMonths`).
const AVG_DAYS_PER_MONTH = 146097 / 4800;

// Moment's default English relative-time thresholds/wording
// (src/lib/duration/humanize.js), reimplemented so `fromNow()` output matches
// what this app already displays and asserts in tests. Below the month
// threshold this is a straight ms-based bucket; at and above it, matching
// moment requires the same hybrid it uses internally: a whole calendar-month
// count plus the remaining days converted via the fixed average above, not a
// flat days/30 (or days/30.44) estimate — verified against real `moment()`
// output across thousands of day offsets and multiple anchor dates.
function relativeTimePhrase(now: Date, target: Date): string {
  const diffMs = target.getTime() - now.getTime();
  const seconds = Math.round(Math.abs(diffMs) / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);

  if (seconds < 45) return "a few seconds";
  if (seconds < 90) return "a minute";
  if (minutes < 45) return `${minutes} minutes`;
  if (minutes < 90) return "an hour";
  if (hours < 22) return `${hours} hours`;
  if (hours < 36) return "a day";
  if (days < 26) return `${days} days`;

  const earlier = now < target ? now : target;
  const later = now < target ? target : now;
  const { months, remainderMs } = calendarMonthDiffWithRemainder(
    later,
    earlier,
    false,
  );
  const totalMonths = Math.abs(
    months + remainderMs / (DAY_MS * AVG_DAYS_PER_MONTH),
  );
  const roundedMonths = Math.round(totalMonths);

  if (roundedMonths <= 1) return "a month";
  if (roundedMonths < 11) return `${roundedMonths} months`;

  const years = Math.round(totalMonths / 12);
  if (years <= 1) return "a year";
  return `${years} years`;
}

export function fromNow(input: DateInput): string {
  const target = parseDate(input);
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const phrase = relativeTimePhrase(now, target);
  return diffMs < 0 ? `${phrase} ago` : `in ${phrase}`;
}
